/**
 * useUpdateMaintenanceIssue — direct UPDATE on maintenance_issues.
 *
 * Maintenance-role RLS already restricts UPDATE to the user's own
 * assigned issues in their org (migration 20260501000005b §maintenance_
 * issues_update); supervisors and admins have broader UPDATE access.
 * This hook is a thin transport — no business-logic enforcement client-
 * side beyond timestamp + minimum-fields population. The DB is the
 * authority.
 *
 * Allowed status transitions are not enforced here — RLS will simply
 * reject an unauthorised actor. The audit trail goes through the
 * `audit_logs` table by trigger (existing handle_updated_at + audit
 * triggers on maintenance_issues; see migration 20260501000004).
 *
 * Note (CLAUDE.md ERROR 5 / rule 4): this is **not** writing
 * rooms.current_status — that table is locked behind the
 * room-transition Edge Function. maintenance_issues has its own
 * RLS-controlled UPDATE policy and is allowed.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'
import type { MaintenanceIssueStatus }                          from './useMaintenanceIssues'

interface UpdateVars {
  issueId:         string
  nextStatus:      MaintenanceIssueStatus
  resolutionNotes?: string
}

export interface UpdateMaintenanceResult {
  id:               string
  status:           MaintenanceIssueStatus
  resolved_at:      string | null
  resolution_notes: string | null
}

export function useUpdateMaintenanceIssue(): UseMutationResult<UpdateMaintenanceResult, AppError, UpdateVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<UpdateMaintenanceResult, AppError, UpdateVars>({
    mutationFn: async ({ issueId, nextStatus, resolutionNotes }): Promise<UpdateMaintenanceResult> => {
      const patch: Record<string, unknown> = { status: nextStatus }

      if (nextStatus === 'resolved' || nextStatus === 'closed') {
        const trimmed = (resolutionNotes ?? '').trim()
        if (trimmed.length === 0) {
          throw new AppError({
            code:        'MISSING_RESOLUTION',
            message:     'resolution_notes is required when resolving or closing',
            userMessage: 'Please add a short resolution note before marking this resolved.',
            status:      400,
          })
        }
        patch.resolution_notes = trimmed
        if (nextStatus === 'resolved') {
          patch.resolved_at = new Date().toISOString()
        } else {
          patch.closed_at = new Date().toISOString()
        }
      }

      const { data, error } = await supabase
        .from('maintenance_issues')
        .update(patch)
        .eq('id', issueId)
        .select('id, status, resolved_at, resolution_notes')
        .single()

      if (error) {
        const code = mapPgErrorCode(error.code)
        throw new AppError({
          code,
          message:     error.message,
          userMessage: userMessageFor(code, error.message),
          status:      403,
        })
      }
      return data as unknown as UpdateMaintenanceResult
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['maintenance-issues'] })
      void queryClient.invalidateQueries({ queryKey: ['room-board']         })
    },
  })
}

function mapPgErrorCode(pgCode: string | undefined): string {
  switch (pgCode) {
    case '42501': return 'FORBIDDEN'         // RLS denied
    case 'PGRST116': return 'NOT_FOUND'      // no rows updated
    default:      return 'UPDATE_FAILED'
  }
}

function userMessageFor(code: string, fallback: string): string {
  switch (code) {
    case 'FORBIDDEN':         return 'You do not have permission to update this issue.'
    case 'NOT_FOUND':         return 'This issue could not be found or you cannot see it anymore.'
    case 'MISSING_RESOLUTION':return 'A resolution note is required.'
    default:                  return fallback || 'Could not update the maintenance issue.'
  }
}
